import * as d3 from 'd3';
import { Selection, BaseType } from 'd3';

// D3のセレクションの型を短く定義
type D3Selection<T extends BaseType> = Selection<T, unknown, null, undefined>;

/**
 * グラフの初期設定オブジェクトの型定義
 */
interface ChartConfig {
	jsonUrl: string | null;
	animationDurationPerSeries: number;
	autoplay: boolean;
	autoplayDelay: number;
	// 子クラスで追加される可能性のある、その他の設定を許容する
	[key: string]: any;
}

/**
 * UI更新時にイニシャライザに渡される状態オブジェクトの型定義
 */
export interface UIState {
	isPlaying: boolean;
	currentFrame: number;
	totalFrames: number;
	// 子クラスで追加される可能性のある、その他の状態を許容する
	[key: string]: any;
}

/**
 * すべてのアニメーション付きグラフの基底クラス (Abstract Class).
 * 共通のライフサイクル、アニメーション制御、UI連携の仕組みを提供する。
 *
 * @abstract
 * @template TData - d3.jsonで読み込むデータセット全体の型
 * @template TSeries - 描画用に処理された単一または複数の系列データの型
 * @template TUIState - UIに通知する状態オブジェクトの型 (UIStateを継承)
 */
export abstract class BaseChart<
	TData,
	TSeries,
	TUIState extends UIState = UIState
> {
	// --- プロパティ定義 ---
	protected chartElement: HTMLElement;
	protected config: ChartConfig;
	protected margin: { top: number; right: number; bottom: number; left: number };
	protected width: number;
	protected height: number;
	protected svg: D3Selection<SVGSVGElement> | null;
	protected g: D3Selection<SVGGElement> | null;

	protected fullDataSet: TData | null;
	protected activeLineData: TSeries[];
	protected isPlaying: boolean;
	protected animationFrameId: number | null;
	protected totalFrames: number;
	protected currentFrame: number;

	public isReady: boolean;
	protected onUpdateUICallback: (state: TUIState) => void;

	// 子クラスで上書きされることを想定したプロパティ
	static defaultConfig: Partial<ChartConfig> = {};

	/**
	 * BaseChartのコンストラクタ
	 * @param {HTMLElement} chartElement - グラフを描画するコンテナ要素
	 * @param {object} [config={}] - グラフのカスタム設定を格納するオブジェクト
	 */
	constructor(chartElement: HTMLElement, config: Partial<ChartConfig> = {}) {
		if (new.target === BaseChart) {
			throw new TypeError('BaseChart is an abstract class and cannot be instantiated directly.');
		}
		this.chartElement = chartElement;

		const baseDefaultConfig: ChartConfig = {
			jsonUrl: chartElement.dataset.jsonUrl || null,
			animationDurationPerSeries: 2000,
			autoplay: true,
			autoplayDelay: 500,
		};

		this.config = Object.assign({}, baseDefaultConfig, (this.constructor as typeof BaseChart).defaultConfig, config);

		this.margin = { top: 20, right: 40, bottom: 50, left: 60 };
		this.width = 0;
		this.height = 0;
		this.svg = null;
		this.g = null;

		this.fullDataSet = null;
		this.activeLineData = [];
		this.isPlaying = false;
		this.animationFrameId = null;
		this.totalFrames = 0;
		this.currentFrame = 0;

		this.isReady = false;
		this.onUpdateUICallback = () => {};
	}

	/**
	 * グラフの初期化処理（非同期）
	 */
	public async init(): Promise<void> {
		if (!this.config.jsonUrl) {
			this._displayMessage('データURLが指定されていません。', 'error');
			return;
		}

		if (!this.fullDataSet) {
			try {
				// d3.jsonにジェネリクスで型を指定し、nullの可能性を考慮する
				this.fullDataSet = await d3.json<TData>(this.config.jsonUrl) ?? null;
			} catch (error) {
				this._displayMessage('データの読み込みに失敗しました。', 'error');
				console.error("Failed to load chart data:", error);
				throw error;
			}
		}

		// 【修正】ローディングメッセージを Role属性 で取得して非表示にする
		const loadingMessage = this.chartElement.querySelector<HTMLElement>('[data-chart-role="loading"]');
		if (loadingMessage) {
			loadingMessage.style.display = 'none';
		}

		this._processData();
		this.setupDimensions();
		this.setupLayers();
		this.resetAndRedraw();
	}

	/**
	 * 描画エリアの寸法を計算する
	 */
	protected setupDimensions(): void {
		const containerRect = this.chartElement.getBoundingClientRect();
		this.width = containerRect.width - this.margin.left - this.margin.right;
		this.height = containerRect.height - this.margin.top - this.margin.bottom;
	}

	/**
	 * アニメーションをリセットし、再描画する
	 */
	protected resetAndRedraw(): void {
		this.pause();
		this.currentFrame = 0;
		this._initialDrawAxes();

		const framesPerSeries = (this.config.animationDurationPerSeries / 1000) * 60;
		this.totalFrames = framesPerSeries * (this.activeLineData?.length || 1);

		this.isReady = true;
		this.drawFrame(0);
		this.updateUI();

		if (this.config.autoplay) {
			setTimeout(() => this.play(), this.config.autoplayDelay);
		}
	}

	/**
	 * アニメーションを開始する
	 */
	public play(): void {
		if (this.isPlaying || !this.isReady) return;
		this.isPlaying = true;
		if (this.currentFrame >= this.totalFrames - 1) {
			this.currentFrame = 0;
		}
		this.animationFrameId = requestAnimationFrame(() => this.animationLoop());
		this.updateUI();
	}

	/**
	 * アニメーションを一時停止する
	 */
	public pause(): void {
		if (!this.isPlaying || !this.animationFrameId) return;
		this.isPlaying = false;
		cancelAnimationFrame(this.animationFrameId);
		this.animationFrameId = null;
		this.updateUI();
	}

	/**
	 * アニメーションの再生/一時停止を切り替える
	 */
	public togglePlayPause(): void {
		if (this.isPlaying) {
			this.pause();
		} else {
			this.play();
		}
	}

	/**
	 * 指定したフレームに移動する
	 * @param {number} frame - 移動先のフレーム番号
	 */
	public seek(frame: number): void {
		if (!this.isReady) return;
		this.pause();
		this.currentFrame = Math.max(0, Math.min(frame, this.totalFrames - 1));
		this.drawFrame(this.currentFrame);
		this.updateUI();
	}

	/**
	 * アニメーションのループ処理
	 */
	protected animationLoop(): void {
		if (!this.isPlaying) return;
		this.drawFrame(this.currentFrame);
		this.updateUI();

		this.currentFrame++;
		if (this.currentFrame >= this.totalFrames) {
			this.pause();
		} else {
			this.animationFrameId = requestAnimationFrame(() => this.animationLoop());
		}
	}

	/**
	 * イニシャライザ（UI側）に現在の状態を通知するためのコールバックを登録する
	 */
	public onUpdateUI(callback: (state: TUIState) => void): void {
		this.onUpdateUICallback = callback;
	}

	/**
	 * 現在の状態をUIに通知する。
	 * 子クラスでUIStateを拡張する場合は、このメソッドをオーバーライドして
	 * 独自のプロパティを持つstateオブジェクトを渡すことが推奨される。
	 */
	protected updateUI(): void {
		if (typeof this.onUpdateUICallback === 'function') {
			const basicState: UIState = {
				isPlaying: this.isPlaying,
				currentFrame: this.currentFrame,
				totalFrames: this.totalFrames,
			};
			// TUIStateはUIStateを継承しているため、基本stateはTUIStateとしてキャスト可能
			this.onUpdateUICallback(basicState as TUIState);
		}
	}

	/**
	 * ウィンドウリサイズ時に呼ばれる再描画処理
	 */
	public redrawOnResize(): void {
		if (this.isPlaying) {
			this.pause();
		}
		this.init();
	}

	/**
	 * エラーメッセージなどをグラフエリアに表示するヘルパー
	 */
	protected _displayMessage(message: string, type: 'error' | 'warning' | 'info' = 'error'): void {
		// 【修正】Role属性を使って古いメッセージを削除
		d3.select(this.chartElement).select('[data-chart-role="message"]').remove();
		// 【修正】新規メッセージにもRole属性を付与
		d3.select(this.chartElement).append('p')
			.attr('class', `chart-message ${type}`)
			.attr('data-chart-role', 'message')
			.text(message);
	}

	// --- 子クラスでの実装が必須な抽象メソッド ---
	protected abstract _processData(): void;
	protected abstract setupLayers(): void;
	protected abstract _initialDrawAxes(): void;
	protected abstract drawFrame(frame: number): void | Promise<void>;
}